import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { MacroCatalogOverview, MacroImportHistoryItem, macroDataClient } from "../../Pages/MacroData/macroDataClient";

/**
 * Cache de l'écran « Données macroéconomiques », même forme que CategoriesSlice (voir le
 * commentaire de référence là-bas) : l'historique et le catalogue sont chargés UNE fois puis
 * survivent à la navigation. Sans cela, chaque retour sur /macro-data relançait deux appels
 * (dont un catalogue qui agrège 14 sujets), ce qui se voit dès qu'on est plusieurs sur l'admin.
 *
 *  - `loading`      premier chargement automatique : l'écran montre son squelette ;
 *  - `isRefreshing` rechargement manuel : les données restent affichées, seul le bouton tourne ;
 *  - `initialized`  distingue « jamais chargé » de « chargé et réellement vide ».
 * Toute écriture (validation, confirmation, publication) rafraîchit en mode manuel.
 */
export interface MacroDataState {
	imports: MacroImportHistoryItem[];
	catalog: MacroCatalogOverview | null;
	loading: boolean;
	isRefreshing: boolean;
	error?: string | null;
	initialized: boolean;
	lastUpdated: string | null;
}

const initialState: MacroDataState = {
	imports: [],
	catalog: null,
	loading: false,
	isRefreshing: false,
	error: null,
	initialized: false,
	lastUpdated: null
};

const macroDataSlice = createSlice({
	name: "macroData",
	initialState,
	reducers: {
		setLoading(state, action: PayloadAction<boolean>) {
			state.loading = action.payload;
		},
		setRefreshing(state, action: PayloadAction<boolean>) {
			state.isRefreshing = action.payload;
		},
		setMacroData(state, action: PayloadAction<{ imports: MacroImportHistoryItem[]; catalog: MacroCatalogOverview | null }>) {
			state.imports = action.payload.imports ?? [];
			// Un catalogue indisponible (droit manquant, erreur ponctuelle) ne doit pas effacer
			// celui déjà affiché : on ne remplace que lorsqu'une valeur fraîche est arrivée.
			if (action.payload.catalog) state.catalog = action.payload.catalog;
			state.initialized = true;
			state.error = null;
			state.lastUpdated = new Date().toISOString();
		},
		setError(state, action: PayloadAction<string | null>) {
			state.error = action.payload;
			state.loading = false;
			state.isRefreshing = false;
		}
	}
});

export const { setLoading, setRefreshing, setMacroData, setError } = macroDataSlice.actions;
export const { reducer } = macroDataSlice;

/** Charge historique + catalogue en parallèle. `manual` pilote le drapeau de rafraîchissement. */
export const fetchMacroData = (manual = false) => async (dispatch: Dispatch) => {
	dispatch(manual ? setRefreshing(true) : setLoading(true));
	try {
		const [imports, catalog] = await Promise.all([
			macroDataClient.listImports(),
			macroDataClient.getCatalog().catch(() => null)
		]);
		dispatch(setMacroData({ imports, catalog }));
	} catch (err: any) {
		dispatch(setError(err?.response?.data?.message ?? err?.message ?? "Impossible de charger les imports macroéconomiques."));
	} finally {
		dispatch(manual ? setRefreshing(false) : setLoading(false));
	}
};
